/**
 * Logo component for sidebar/navigation and auth pages.
 * Displays the CoOrdio Cortex logo (C-node icon + wordmark) per CoOrdio Design System.
 */

// React Imports
import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';

// Third-party Imports
import styled from '@emotion/styled';

// Config Imports
import themeConfig from '@configs/themeConfig';

// Hook Imports
import { useSettings } from '@core/hooks/useSettings';

// MUI Imports
import { Box } from '@mui/material';

/** CoOrdio Cortex accent (rose) for icon and "Cortex" wordmark */
const CORTEX_ACCENT = '#F43F5E';

/** CoOrdio Design System C-node icon SVG (standalone, accent color) */
const CortexIconSvg = ({ size = 32, className }: { size?: number; className?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden
  >
    <path
      d="M 75 30 A 35 35 0 1 0 75 70"
      stroke={CORTEX_ACCENT}
      strokeWidth="16"
      strokeLinecap="round"
      fill="none"
    />
    <circle cx="50" cy="50" r="12" fill={CORTEX_ACCENT} />
    <path d="M 50 25 L 50 38" stroke={CORTEX_ACCENT} strokeWidth="12" strokeLinecap="round" />
    <path d="M 50 75 L 50 62" stroke={CORTEX_ACCENT} strokeWidth="12" strokeLinecap="round" />
    <path d="M 75 50 L 62 50" stroke={CORTEX_ACCENT} strokeWidth="12" strokeLinecap="round" />
  </svg>
);

type LogoTextProps = {
  isCollapsed?: boolean;
  isHovered?: boolean;
  transitionDuration?: number;
  color?: CSSProperties['color'];
  /** When true, render "CoOrdio " in primary color and "Cortex" in accent (wordmark style) */
  wordmarkStyle?: boolean;
};

const LogoText = styled.span<LogoTextProps>`
  color: ${({ color }) => color ?? 'var(--mui-palette-text-primary)'};
  font-size: 1.375rem;
  line-height: 1.09091;
  font-weight: 700;
  letter-spacing: 0.25px;
  transition: ${({ transitionDuration = 300 }) =>
    `margin-inline-start ${transitionDuration}ms ease-in-out, opacity ${transitionDuration}ms ease-in-out`};

  ${({ isHovered, isCollapsed }) =>
    isCollapsed && !isHovered
      ? 'opacity: 0; margin-inline-start: 0;'
      : 'opacity: 1; margin-inline-start: 12px;'}
`;

/**
 * Logo component props
 */
interface LogoProps {
  color?: CSSProperties['color'];
  isCollapsed?: boolean;
  isHovered?: boolean;
  transitionDuration?: number;
  /** Icon size in px (default 32) */
  iconSize?: number;
  /** Use CoOrdio wordmark style: "CoOrdio " in primary, "Cortex" in accent */
  wordmarkStyle?: boolean;
  /** Hide text (icon only) */
  iconOnly?: boolean;
}

/**
 * Logo component.
 * Displays CoOrdio Cortex logo (C-node icon + wordmark) that animates based on sidebar state when used in nav.
 */
const Logo = ({
  color,
  isCollapsed = false,
  isHovered = false,
  transitionDuration = 300,
  iconSize = 32,
  wordmarkStyle = false,
  iconOnly = false,
}: LogoProps) => {
  const logoTextRef = useRef<HTMLSpanElement>(null);
  const { settings } = useSettings();
  const { layout } = settings;
  const isLayoutCollapsed = layout === 'collapsed' || isCollapsed;

  useEffect(() => {
    if (!isLayoutCollapsed) return;
    if (logoTextRef?.current) {
      if (isLayoutCollapsed && !isHovered) {
        logoTextRef.current.classList.add('hidden');
      } else {
        logoTextRef.current.classList.remove('hidden');
      }
    }
  }, [isHovered, isLayoutCollapsed]);

  return (
    <Box className="flex items-center" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={{
          width: iconSize,
          height: iconSize,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CortexIconSvg size={iconSize} />
      </Box>
      {!iconOnly && (
        wordmarkStyle ? (
          <LogoText
            color={color}
            ref={logoTextRef}
            isHovered={isHovered}
            isCollapsed={isLayoutCollapsed}
            transitionDuration={transitionDuration}
          >
            <span style={{ color: color ?? 'var(--mui-palette-text-primary)' }}>CoOrdio </span>
            <span style={{ color: CORTEX_ACCENT }}>Cortex</span>
          </LogoText>
        ) : (
          <LogoText
            color={color}
            ref={logoTextRef}
            isHovered={isHovered}
            isCollapsed={isLayoutCollapsed}
            transitionDuration={transitionDuration}
          >
            {themeConfig.templateName}
          </LogoText>
        )
      )}
    </Box>
  );
};

export default Logo;
export { CortexIconSvg };
